import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { FfiecEnrichment } from '@spoofio/shared'
import { api } from '@/services/api'
import { Card, Button, Badge, ConfidenceBadge } from '@/components/ui'
import { formatAuditTimestamp } from '@/utils/format-date'

export function DetectionDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data: detection, isLoading, error } = useQuery({
    queryKey: ['detection', id],
    queryFn: () => api.getDetection(id ?? ''),
    enabled: !!id,
  })

  const dismissMutation = useMutation({
    mutationFn: () => api.dismissDetection(id ?? ''),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['detection', id] })
      void queryClient.invalidateQueries({ queryKey: ['detections'] })
    },
  })

  if (isLoading) {
    return <DetectionDetailSkeleton />
  }

  if (error || !detection) {
    return (
      <div className="text-center py-12">
        <p className="text-danger-600">Failed to load detection</p>
        <Button variant="secondary" className="mt-4" onClick={() => { navigate('/detections'); }}>
          Back to Detections
        </Button>
      </div>
    )
  }

  // SECURITY: Safely parse filter signals with error handling
  // Signals may be strings or objects with {type, score, description}
  type FilterSignalObj = { type: string; score: number; description: string }
  let filterSignals: (string | FilterSignalObj)[] = []
  if (typeof detection.filterSignals === 'string') {
    try {
      filterSignals = JSON.parse(detection.filterSignals) as (string | FilterSignalObj)[]
    } catch {
      // Malformed JSON - use empty array
      filterSignals = []
    }
  } else if (Array.isArray(detection.filterSignals)) {
    filterSignals = detection.filterSignals as (string | FilterSignalObj)[]
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/detections" className="text-gray-400 hover:text-gray-600">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Detection Details</h1>
            <p className="text-sm text-gray-500">
              {formatAuditTimestamp(detection.detectedAt)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {!detection.alertSent && (
            <Button
              variant="secondary"
              onClick={() => { dismissMutation.mutate(); }}
              isLoading={dismissMutation.isPending}
            >
              Mark as False Positive
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Detection Summary */}
        <Card title="Detection Summary">
          <dl className="space-y-4">
            <div className="flex justify-between">
              <dt className="text-sm text-gray-500">Fake Domain</dt>
              <dd className="text-sm font-mono font-medium">{detection.fakeFqdn}</dd>
            </div>
            {detection.fakeUrl && (
              <div className="flex justify-between items-start">
                <dt className="text-sm text-gray-500">Full URL</dt>
                <dd className="text-sm font-mono break-all max-w-xs text-right">
                  {detection.fakeUrl}
                </dd>
              </div>
            )}
            <div className="flex justify-between">
              <dt className="text-sm text-gray-500">Confidence Score</dt>
              <dd><ConfidenceBadge score={detection.confidenceScore} /></dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-sm text-gray-500">Canary</dt>
              <dd>
                <Link
                  to={`/canaries/${detection.canaryId}`}
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  {detection.canaryName}
                </Link>
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-sm text-gray-500">Organization</dt>
              <dd className="text-sm font-medium">{detection.organizationName}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-sm text-gray-500">Alert Status</dt>
              <dd>
                <Badge variant={detection.alertSent ? 'success' : 'gray'}>
                  {detection.alertSent ? 'Alert Sent' : 'No Alert'}
                </Badge>
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-sm text-gray-500">Detection Method</dt>
              <dd>
                <Badge variant={detection.detectionMethod === 'pixel' ? 'gray' : 'primary'}>
                  {detection.detectionMethod === 'css' ? 'CSS Asset' :
                   detection.detectionMethod === 'js' ? 'JS Asset' :
                   'Pixel (Legacy)'}
                </Badge>
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-sm text-gray-500">Source Method</dt>
              <dd>
                <Badge variant={
                  detection.sourceMethod === 'param' ? 'success' :
                  detection.sourceMethod === 'referer' ? 'warning' :
                  'gray'
                }>
                  {detection.sourceMethod === 'param' ? 'URL Parameter' :
                   detection.sourceMethod === 'referer' ? 'Referer Header' :
                   'Unknown'}
                </Badge>
              </dd>
            </div>
          </dl>
        </Card>

        {/* Visitor Information */}
        <Card title="Visitor Information">
          <dl className="space-y-4">
            <div className="flex justify-between">
              <dt className="text-sm text-gray-500">IP Address</dt>
              <dd className="text-sm font-mono">{detection.visitorIp}</dd>
            </div>
            {detection.originalVisitorIp && (
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Original IP</dt>
                <dd className="text-sm font-mono">{detection.originalVisitorIp}</dd>
              </div>
            )}
            <div className="flex justify-between">
              <dt className="text-sm text-gray-500">Country</dt>
              <dd className="text-sm font-medium">
                {detection.cfCountry ?? 'Unknown'}
              </dd>
            </div>
            {detection.cfCity && (
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">City</dt>
                <dd className="text-sm font-medium">{detection.cfCity}</dd>
              </div>
            )}
            {detection.cfRegion && (
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Region</dt>
                <dd className="text-sm font-medium">{detection.cfRegion}</dd>
              </div>
            )}
            {detection.cfRay && (
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">CF-Ray</dt>
                <dd className="text-sm font-mono text-gray-500">{detection.cfRay}</dd>
              </div>
            )}
          </dl>
        </Card>

        {/* User Agent */}
        <Card title="User Agent">
          {detection.userAgent ? (
            <p className="text-sm font-mono break-all text-gray-600">
              {detection.userAgent}
            </p>
          ) : (
            <p className="text-sm text-gray-400">Not provided</p>
          )}
        </Card>

        {/* Referrer */}
        <Card title="Referrer">
          {detection.referrer ? (
            <p className="text-sm font-mono break-all text-gray-600">
              {detection.referrer}
            </p>
          ) : (
            <p className="text-sm text-gray-400">Not provided (direct access)</p>
          )}
        </Card>

        {/* Filter Signals */}
        <Card title="Confidence Analysis" className="lg:col-span-2">
          <p className="text-sm text-gray-500 mb-4">
            These signals contributed to the confidence score of {detection.confidenceScore}%
          </p>
          {filterSignals.length > 0 ? (
            <ul className="space-y-2">
              {filterSignals.map((signal, index) => {
                const isObj = typeof signal === 'object'
                const description = isObj ? signal.description : signal
                const score = isObj ? signal.score : undefined

                return (
                  <li key={index} className="flex items-start gap-2">
                    <span className="flex-shrink-0 w-2 h-2 rounded-full bg-primary-500 mt-2" />
                    <span className="text-sm text-gray-700">
                      {description}
                      {score !== undefined && (
                        <span className="ml-2 text-xs text-gray-400">({score})</span>
                      )}
                    </span>
                  </li>
                )
              })}
            </ul>
          ) : (
            <p className="text-sm text-gray-400">No filter signals recorded</p>
          )}
        </Card>

        {/* FFIEC Enhanced Detection */}
        {detection.ffiecEnrichment && (
          <FfiecEnrichmentPanel enrichment={detection.ffiecEnrichment} />
        )}

        {/* Screenshot */}
        {detection.screenshotUrl && (
          <Card title="Screenshot" className="lg:col-span-2">
            <div className="bg-gray-50 rounded-lg p-4">
              <AuthenticatedScreenshot detectionId={detection.id} />
            </div>
          </Card>
        )}
      </div>
    </div>
  )
}

function FfiecEnrichmentPanel({ enrichment }: { enrichment: FfiecEnrichment }) {
  return (
    <Card title="FFIEC Enhanced Detection" className="lg:col-span-2">
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {/* Radar verdict */}
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">Cloudflare Radar</h4>
          <dl className="space-y-2">
            <div className="flex justify-between">
              <dt className="text-sm text-gray-500">Verdict</dt>
              <dd>
                <Badge variant={
                  enrichment.radarVerdict === 'malicious' || enrichment.radarVerdict === 'phishing' ? 'danger' :
                  enrichment.radarVerdict === 'clean' ? 'success' : 'gray'
                }>
                  {enrichment.radarVerdict ?? 'Unknown'}
                </Badge>
              </dd>
            </div>
            {enrichment.radarScanUrl && (
              <div className="flex justify-between items-start">
                <dt className="text-sm text-gray-500">Scan</dt>
                <dd>
                  <a href={enrichment.radarScanUrl} target="_blank" rel="noopener noreferrer"
                    className="text-sm text-primary-600 hover:text-primary-700">
                    View report
                  </a>
                </dd>
              </div>
            )}
            {enrichment.hostingIp && (
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Hosting IP</dt>
                <dd className="text-sm font-mono">{enrichment.hostingIp}</dd>
              </div>
            )}
          </dl>
        </div>

        {/* Hosting intelligence */}
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">Hosting</h4>
          <dl className="space-y-2">
            {enrichment.hostingIsp && (
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">ISP</dt>
                <dd className="text-sm font-medium text-right max-w-[160px] truncate" title={enrichment.hostingIsp}>{enrichment.hostingIsp}</dd>
              </div>
            )}
            {enrichment.hostingOrg && (
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Org</dt>
                <dd className="text-sm font-medium text-right max-w-[160px] truncate" title={enrichment.hostingOrg}>{enrichment.hostingOrg}</dd>
              </div>
            )}
            {enrichment.hostingAsn && (
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">ASN</dt>
                <dd className="text-sm font-mono">{enrichment.hostingAsn}</dd>
              </div>
            )}
            {enrichment.hostingCountry && (
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Country</dt>
                <dd className="text-sm font-medium">{enrichment.hostingCountry}</dd>
              </div>
            )}
            {enrichment.hostingAbuseEmail && (
              <div className="flex justify-between items-center">
                <dt className="text-sm text-gray-500">Abuse</dt>
                <dd>
                  <a href={`mailto:${enrichment.hostingAbuseEmail}`}
                    className="text-sm text-primary-600 hover:text-primary-700 font-mono">
                    {enrichment.hostingAbuseEmail}
                  </a>
                </dd>
              </div>
            )}
          </dl>
        </div>

        {/* Domain registration */}
        <div>
          <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">Domain Registration</h4>
          <dl className="space-y-2">
            {enrichment.registrar && (
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Registrar</dt>
                <dd className="text-sm font-medium text-right max-w-[160px] truncate" title={enrichment.registrar}>{enrichment.registrar}</dd>
              </div>
            )}
            {enrichment.registrarAbuseEmail && (
              <div className="flex justify-between items-center">
                <dt className="text-sm text-gray-500">Abuse</dt>
                <dd>
                  <a href={`mailto:${enrichment.registrarAbuseEmail}`}
                    className="text-sm text-primary-600 hover:text-primary-700 font-mono">
                    {enrichment.registrarAbuseEmail}
                  </a>
                </dd>
              </div>
            )}
            {enrichment.registrationDate && (
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Registered</dt>
                <dd className="text-sm font-medium">{enrichment.registrationDate.slice(0, 10)}</dd>
              </div>
            )}
            {enrichment.expirationDate && (
              <div className="flex justify-between">
                <dt className="text-sm text-gray-500">Expires</dt>
                <dd className="text-sm font-medium">{enrichment.expirationDate.slice(0, 10)}</dd>
              </div>
            )}
          </dl>
        </div>

        {/* Threat intelligence */}
        <div className="sm:col-span-2 lg:col-span-3 border-t pt-4">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">Threat Intelligence</h4>
          <div className="flex flex-wrap gap-4 items-center">
            <span className="flex items-center gap-2 text-sm">
              <Badge variant={enrichment.phishTankKnown ? 'danger' : 'gray'}>
                PhishTank: {enrichment.phishTankKnown ? 'Known phishing' : 'Not listed'}
              </Badge>
              {!enrichment.phishTankKnown && (
                <a href={enrichment.phishTankSubmitUrl} target="_blank" rel="noopener noreferrer"
                  className="text-xs text-primary-600 hover:text-primary-700">Submit →</a>
              )}
            </span>
            <span className="flex items-center gap-2 text-sm">
              <Badge variant={enrichment.urlhausKnown ? 'danger' : 'gray'}>
                URLhaus: {enrichment.urlhausKnown ? 'Known malware' : 'Not listed'}
              </Badge>
              {!enrichment.urlhausKnown && (
                <a href={enrichment.urlhausSubmitUrl} target="_blank" rel="noopener noreferrer"
                  className="text-xs text-primary-600 hover:text-primary-700">Submit →</a>
              )}
            </span>
            <span className="text-xs text-gray-400 ml-auto">Enriched {formatAuditTimestamp(enrichment.enrichedAt)}</span>
          </div>
          {enrichment.errors.length > 0 && (
            <p className="mt-2 text-xs text-warning-600">
              Partial data — some sources failed: {enrichment.errors.join(', ')}
            </p>
          )}
        </div>
      </div>
    </Card>
  )
}

function AuthenticatedScreenshot({ detectionId }: { detectionId: string }) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null)
  const [error, setError] = useState(false)

  useEffect(() => {
    let revoke: string | null = null

    void api.fetchScreenshot(detectionId).then((blob) => {
      const url = URL.createObjectURL(blob)
      revoke = url
      setBlobUrl(url)
    }).catch(() => {
      setError(true)
    })

    return () => { if (revoke) URL.revokeObjectURL(revoke) }
  }, [detectionId])

  if (error) return <p className="text-sm text-gray-400">Screenshot unavailable</p>
  if (!blobUrl) return <div className="h-48 bg-gray-100 rounded animate-pulse" />

  return (
    <img
      src={blobUrl}
      alt="Screenshot of fake page"
      className="max-w-full h-auto rounded border border-gray-200"
    />
  )
}

function DetectionDetailSkeleton() {
  return (
    <div className="space-y-6">
      <div className="h-8 bg-gray-200 rounded w-64 animate-pulse" />
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {Array.from({ length: 4 }, (_unused: unknown, i: number) => (
          <div key={i} className="bg-white shadow rounded-lg p-6 animate-pulse">
            <div className="h-6 bg-gray-200 rounded w-32 mb-4" />
            <div className="space-y-3">
              {Array.from({ length: 5 }, (_unused: unknown, j: number) => (
                <div key={j} className="h-5 bg-gray-100 rounded" />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

